<template lang="pug">
aiq-dialog.add-question-modal(
    :title="$t(`${baseTranslationPath}.add_question_title`)"
    :model-value="modelValue"
    :show-close="true"
    :close-on-click-modal="false"
    width="480px"
    @update:model-value="close"
    @closed="reset"
)
    .add-question-form
        .q-row
            label {{ $t(`${baseTranslationPath}.prompt`) }} *
            aiq-input(
                v-model="form.prompt"
                type="textarea"
                :rows="2"
                :placeholder="$t(`${baseTranslationPath}.prompt_ph`)"
            )
        .q-row
            label {{ $t(`${baseTranslationPath}.answer_type`) }} *
            aiq-select(v-model="form.answer_type" style="width: 100%" filterable)
                aiq-option(v-for="t in answerTypes" :key="t" :label="t" :value="t")
        .q-row
            label {{ $t(`${baseTranslationPath}.interaction_class`) }} *
            aiq-select(v-model="form.interaction_class" style="width: 100%" filterable)
                aiq-option(v-for="c in interactionClasses" :key="c" :label="c" :value="c")
            .q-hint {{ $t(`${baseTranslationPath}.interaction_class_hint`) }}
        .q-row
            label {{ $t(`${baseTranslationPath}.group_type`) }}
            aiq-select(v-model="form.group_type" style="width: 100%" clearable filterable)
                aiq-option(v-for="g in groupTypes" :key="g" :label="g" :value="g")
        .q-row.inline
            aiq-switch(v-model="form.required")
            span {{ $t(`${baseTranslationPath}.required`) }}
        .q-row
            label {{ $t(`${baseTranslationPath}.target_fields`) }} *
            aiq-select(
                v-model="form.target_field_ids"
                style="width: 100%"
                multiple
                filterable
                :reserve-keyword="false"
                :placeholder="$t(`${baseTranslationPath}.target_fields_ph`)"
            )
                aiq-option(
                    v-for="f in fields"
                    :key="f.field_key"
                    :label="fieldLabel(f)"
                    :value="f.field_key"
                )
            .q-hint(v-if="!fields.length") {{ $t(`${baseTranslationPath}.no_unlinked_fields`) }}
        .actions
            aiq-button(@click="close") {{ $t('global.cancel') }}
            aiq-button(type="primary" :loading="saving" :disabled="!canSubmit" @click="submit") {{ $t(`${baseTranslationPath}.add_question`) }}
</template>
<script>
import get from 'lodash/get';

import { fieldOptionLabel } from './fieldUtil';

const ANSWER_TYPES = ['text', 'checkbox', 'radio', 'date', 'currency', 'phone', 'email',
  'account_number', 'routing_number', 'signature', 'initials', 'table_cell', 'computed',
  'internal_only', 'table'];
const INTERACTION_CLASSES = ['simple_input', 'long_text', 'boolean_choice', 'single_choice',
  'multi_choice', 'signature', 'computed', 'internal_only', 'repeating_row', 'repeating_group',
  'composite_group', 'conditional_detail'];
const GROUP_TYPES = ['radio_group', 'checkbox_set', 'table', 'address', 'date_split', 'other'];

const emptyForm = () => ({
  prompt: '',
  answer_type: '',
  interaction_class: '',
  group_type: null,
  required: false,
  target_field_ids: [],
});

export default {
  name: 'AddQuestionDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    formId: {
      type: [String, Number],
      default: null,
    },
    // fields available to attach (typically fields with no question yet).
    fields: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'created'],
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.manage',
      answerTypes: ANSWER_TYPES,
      interactionClasses: INTERACTION_CLASSES,
      groupTypes: GROUP_TYPES,
      form: emptyForm(),
      // the first target field we last derived answer_type/interaction_class from,
      // so manual edits aren't clobbered unless the first field actually changes.
      lastFirstFieldKey: null,
      saving: false,
    };
  },
  computed: {
    // POST enforces prompt, answer_type, interaction_class and a non-empty
    // target_field_ids; mirror that here so we don't fire an invalid request.
    canSubmit() {
      return !!(this.form.prompt && this.form.prompt.trim())
        && !!this.form.answer_type
        && !!this.form.interaction_class
        && this.form.target_field_ids.length > 0;
    },
  },
  watch: {
    // Answer Type / Interaction Class start as "none". When the first target
    // field is chosen (or changed), derive both from that field — the fields
    // already carry field_type + interaction_class, so no extra lookup is needed.
    'form.target_field_ids': function onTargets(ids) {
      const firstKey = ids && ids.length ? ids[0] : null;
      if (firstKey === this.lastFirstFieldKey) {
        return;
      }
      this.lastFirstFieldKey = firstKey;
      if (!firstKey) {
        this.form.answer_type = '';
        this.form.interaction_class = '';
        return;
      }
      const field = this.fields.find(f => f.field_key === firstKey);
      if (!field) {
        return;
      }
      if (ANSWER_TYPES.includes(field.field_type)) {
        this.form.answer_type = field.field_type;
      }
      if (INTERACTION_CLASSES.includes(field.interaction_class)) {
        this.form.interaction_class = field.interaction_class;
      }
    },
  },
  methods: {
    fieldLabel: fieldOptionLabel,
    close() {
      this.$emit('update:modelValue', false);
    },
    reset() {
      this.form = emptyForm();
      this.lastFirstFieldKey = null;
    },
    async submit() {
      if (!this.canSubmit || !this.formId) {
        return;
      }
      this.saving = true;
      try {
        const body = {
          formId: this.formId,
          prompt: this.form.prompt.trim(),
          answer_type: this.form.answer_type,
          interaction_class: this.form.interaction_class,
          required: this.form.required,
          target_field_ids: this.form.target_field_ids,
        };
        if (this.form.group_type) {
          body.group_type = this.form.group_type;
        }
        const resp = await this.$store.dispatch('agentForms/addFormQuestion', body);
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.question_added`));
        this.$emit('created', get(resp, 'data', null));
        this.close();
      } catch (e) {
        this.$aiq.notify.error(
          `${this.$t(`${this.baseTranslationPath}.question_add_error`)} (${get(e, 'response.data.message', get(e, 'message', ''))})`,
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.add-question-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.q-row {
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 12px;
    font-weight: 600;
  }

  &.inline {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
}

.q-hint {
  font-size: 11px;
  color: $aiq-color-text-secondary;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}
</style>
